import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2, Clock, CheckCircle2, XCircle, ShieldCheck, ArrowRight, AlertTriangle,
} from 'lucide-react';
import { publicAPI } from '@/lib/publicApi';

const NAVY = '#0b1e39';
const GOLD = '#c9a24b';

const Page = ({ children }) => (
  <div className="min-h-screen w-full flex items-center justify-center px-4 py-10"
    style={{ background: `radial-gradient(1200px 600px at 50% -10%, #12284a 0%, ${NAVY} 45%, #060f1f 100%)` }}>
    <div className="w-full max-w-lg">{children}</div>
  </div>
);

const Card = ({ children }) => (
  <div className="overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">{children}</div>
);

const Header = () => (
  <div className="px-7 py-6" style={{ background: NAVY }}>
    <div className="text-[11px] font-semibold tracking-[0.2em] uppercase" style={{ color: GOLD }}>KR Enterprise Group</div>
    <div className="text-white text-xl font-semibold mt-0.5">Application Status</div>
  </div>
);

const STATES = {
  pending: {
    icon: Clock, ring: '#c9a24b',
    badge: 'Under review', badgeBg: '#fef3c7', badgeText: '#92680c',
    title: (n) => `Thanks${n ? ', ' + n : ''} — your application is under review`,
    body: 'Our onboarding team is reviewing your details and selected contract categories. You’ll receive an email the moment a decision is made.',
  },
  approved: {
    icon: CheckCircle2, ring: '#15794a',
    badge: 'Approved', badgeBg: '#dcfce7', badgeText: '#15794a',
    title: (n) => `Congratulations${n ? ', ' + n : ''} — you’re approved!`,
    body: 'Welcome aboard. Check your inbox for a secure email with your portal access so you can begin contracting.',
  },
  rejected: {
    icon: XCircle, ring: '#be123c',
    badge: 'Not approved', badgeBg: '#ffe4e6', badgeText: '#be123c',
    title: () => 'Update on your application',
    body: 'After review, we’re unable to move forward with your application at this time.',
  },
};

export default function ApplicationStatusPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    publicAPI.getApplicationStatus(token)
      .then((r) => { if (active) setData(r.data); })
      .catch((e) => { if (active) setError(e?.response?.data?.detail || 'We couldn’t find an application for this link.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  const meta = data ? (STATES[data.status] || STATES.pending) : null;
  const Icon = meta?.icon || Clock;

  return (
    <Page>
      <Card>
        <Header />
        <div className="px-7 py-10 text-center" data-testid="application-status-page">
          {loading ? (
            <div className="flex h-40 items-center justify-center text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : error ? (
            <div data-testid="application-status-error">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full flex items-center justify-center" style={{ background: '#fff1f2' }}>
                <AlertTriangle size={28} style={{ color: '#be123c' }} />
              </div>
              <h2 className="text-xl font-semibold" style={{ color: NAVY }}>Link not found</h2>
              <p className="text-sm text-slate-500 mt-2">{error}</p>
              <a href="/apply" data-testid="application-status-apply-link"
                className="inline-flex items-center gap-2 mt-6 rounded-lg px-4 py-2.5 text-sm font-semibold text-white"
                style={{ background: NAVY }}>
                Start a new application <ArrowRight size={15} style={{ color: GOLD }} />
              </a>
            </div>
          ) : (
            <>
              <motion.div
                initial={{ scale: 0, rotate: -15 }} animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 200, damping: 13 }}
                className="mx-auto mb-5 h-20 w-20 rounded-full flex items-center justify-center relative"
                style={{ background: '#f4f7fb', border: `2px solid ${meta.ring}` }}
              >
                <Icon size={38} style={{ color: meta.ring }} />
              </motion.div>

              <span
                className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
                style={{ background: meta.badgeBg, color: meta.badgeText }}
                data-testid="application-status-badge"
              >
                {meta.badge}
              </span>

              <h2 className="text-2xl font-semibold mt-4" style={{ color: NAVY }}>
                {meta.title(data.first_name)}
              </h2>
              <p className="text-sm text-slate-600 mt-2 max-w-sm mx-auto">{meta.body}</p>

              {data.status === 'approved' && data.agency_name && (
                <div className="mt-5 inline-flex items-center gap-2 rounded-lg px-4 py-2" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}
                  data-testid="application-status-agency">
                  <ShieldCheck size={15} style={{ color: '#15794a' }} />
                  <span className="text-xs font-medium text-emerald-800">Assigned to {data.agency_name}</span>
                </div>
              )}

              {data.status === 'rejected' && data.rejection_reason && (
                <div className="mt-5 rounded-lg px-4 py-3 text-sm text-rose-800 max-w-sm mx-auto" style={{ background: '#fff1f2', border: '1px solid #fecdd3' }}
                  data-testid="application-status-reason">
                  “{data.rejection_reason}”
                </div>
              )}

              {data.status === 'rejected' && (
                <a href="/apply" data-testid="application-status-reapply-link"
                  className="inline-flex items-center gap-2 mt-6 rounded-lg px-4 py-2.5 text-sm font-semibold text-white"
                  style={{ background: NAVY }}>
                  Submit a new application <ArrowRight size={15} style={{ color: GOLD }} />
                </a>
              )}
            </>
          )}
        </div>
      </Card>
      <p className="text-center text-[11px] text-slate-400 mt-4">© KR Enterprise Group · Confidential &amp; secure onboarding</p>
    </Page>
  );
}
